import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { ArrowRight, Sparkles } from "lucide-react";

export default function Result() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/submissions/${id}`);
        setData(res.data);
      } catch (e) {
        setError(e?.response?.status === 404 ? "Result not found." : "Could not load your result.");
      }
    })();
  }, [id]);

  if (error) {
    return (
      <div>
        <Header />
        <div className="mx-auto max-w-3xl px-6 py-32 text-center" data-testid="result-error">
          <p className="overline mb-6">Something went off-foundation</p>
          <h1 className="font-serif text-4xl">{error}</h1>
          <Link to="/quiz" className="btn-primary mt-10 inline-flex" data-testid="result-restart-link">
            Retake the quiz <ArrowRight size={16} strokeWidth={1.5} />
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="result-loading">
        <p className="overline">Preparing your result…</p>
      </div>
    );
  }

  const isGreatFit = data.fit_result === "great_fit";
  const accent = isGreatFit ? "hsl(var(--gold))" : "hsl(var(--sage))";

  return (
    <div data-testid="result-page">
      <Header />

      <section className="mx-auto max-w-5xl px-6 sm:px-10 pt-20 sm:pt-28" data-testid="result-hero">
        <p className="overline" data-testid="result-greeting">Hello {data.name.split(" ")[0]},</p>
        <h1
          className="font-serif text-5xl sm:text-7xl leading-[1.02] tracking-tight mt-8"
          data-testid="result-headline"
        >
          {isGreatFit ? (
            <>
              You're a <em className="italic" style={{ color: accent }}>strong fit</em>.
            </>
          ) : (
            <>
              Start with the <em className="italic" style={{ color: accent }}>foundations</em>.
            </>
          )}
        </h1>
        <p className="mt-6 font-serif italic text-2xl text-[hsl(var(--plum-soft))]" data-testid="result-headline-detail">
          {data.headline}
        </p>
        <p className="mt-8 max-w-2xl text-xl text-[hsl(var(--plum-soft))] font-light" data-testid="result-subheadline">
          {data.subheadline}
        </p>

        <div className="mt-12 flex flex-wrap gap-4 items-center">
          <a
            href={data.cta_href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="result-primary-cta"
            className="btn-primary"
          >
            {data.cta_label} <ArrowRight size={16} strokeWidth={1.5} />
          </a>
          {isGreatFit ? (
            <a
              href="https://pages.truewellnesswny.com/functional-wellness-mentorship"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="result-secondary-cta"
              className="overline hover:text-[hsl(var(--gold))] transition-colors"
            >
              Or explore the sales page →
            </a>
          ) : (
            <a
              href="https://pages.truewellnesswny.com/functional-wellness-mentorship"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="result-secondary-cta"
              className="overline hover:text-[hsl(var(--gold))] transition-colors"
            >
              Or see the full mentorship →
            </a>
          )}
        </div>
      </section>

      {/* Top 3 benefits */}
      <section className="py-24 sm:py-32 mt-20 rule-top" data-testid="result-benefits-section">
        <div className="mx-auto max-w-5xl px-6 sm:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div>
              <p className="overline">Built around your goals</p>
              <h2 className="font-serif text-4xl sm:text-5xl mt-6">
                What this will mean for you.
              </h2>
            </div>
            <Sparkles size={22} strokeWidth={1.25} color="hsl(var(--gold))" />
          </div>
          <div
            className="grid grid-cols-1 md:grid-cols-3 gap-px"
            style={{ background: "hsl(var(--taupe))" }}
            data-testid="result-benefits-grid"
          >
            {data.top_benefits.map((b, i) => (
              <div
                key={b.tag}
                data-testid={`result-benefit-${i}`}
                className="p-10 flex flex-col gap-6"
                style={{ background: "hsl(var(--ivory))" }}
              >
                <span
                  className="font-serif italic text-2xl"
                  style={{ color: "hsl(var(--gold))" }}
                >
                  0{i + 1}
                </span>
                <h3 className="font-serif text-2xl leading-tight">{b.title}</h3>
                <p className="text-[hsl(var(--plum-soft))] leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Goal recap */}
      {data.goal_labels?.length > 0 && (
        <section className="pb-24 sm:pb-32" data-testid="result-goals-section">
          <div className="mx-auto max-w-5xl px-6 sm:px-10">
            <p className="overline">You told us you want to</p>
            <ul className="mt-6 flex flex-wrap gap-3" data-testid="result-goals-list">
              {data.goal_labels.map((g) => (
                <li
                  key={g}
                  className="px-5 py-3 text-sm"
                  style={{
                    border: "1px solid hsl(var(--taupe))",
                    color: "hsl(var(--plum))",
                    background: "hsl(var(--ivory-2))",
                  }}
                >
                  {g}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Final CTA band */}
      <section className="py-24 rule-top" data-testid="result-final-cta-section">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="font-serif text-4xl sm:text-5xl">
            {isGreatFit ? "Ready when you are." : "Begin where it matters."}
          </h2>
          <p className="mt-6 text-[hsl(var(--plum-soft))] max-w-xl mx-auto">
            {isGreatFit
              ? "See how the mentorship is structured, who it's for, and what your next step looks like."
              : "The foundational classes are self-paced and open now — the perfect first step."}
          </p>
          <a
            href={data.cta_href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="result-final-cta"
            className="btn-primary mt-10 inline-flex"
          >
            {data.cta_label} <ArrowRight size={16} strokeWidth={1.5} />
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
